'use client';

import { notFound } from 'next/navigation';
import Link from 'next/link';
import {
    Container,
    Typography,
    Box,
    Paper,
    Breadcrumbs,
    Chip,
    Grid,
    Button
} from '@mui/material';
import Navigation from '../../../components/Navigation';
import RelatedPages from '../../../components/RelatedPages';
import { use } from 'react';

// Complete operation data
const operationsData = {
    add: {
        title: 'Add Operation',
        emoji: '➕',
        subtitle: 'Insert new values into JSON objects and arrays',
        description: 'The add operation inserts a new value into a JSON document at the specified path. If the path points to an existing array element, the new value is inserted before that element. If the path ends with a hyphen (-), the value is appended to the array.',
        syntax: '{ "op": "add", "path": "/path/to/target", "value": <any JSON value> }',
        examples: [
            {
                title: 'Add to Object',
                description: 'Add a new property to an object',
                patch: '{ "op": "add", "path": "/email", "value": "john@example.com" }',
                before: '{ "name": "John" }',
                after: '{ "name": "John", "email": "john@example.com" }'
            },
            {
                title: 'Add to Array',
                description: 'Append an item to an array',
                patch: '{ "op": "add", "path": "/items/-", "value": "new item" }',
                before: '{ "items": ["a", "b"] }',
                after: '{ "items": ["a", "b", "new item"] }'
            },
            {
                title: 'Insert at Index',
                description: 'Insert at a specific array position',
                patch: '{ "op": "add", "path": "/items/1", "value": "inserted" }',
                before: '{ "items": ["a", "b", "c"] }',
                after: '{ "items": ["a", "inserted", "b", "c"] }'
            }
        ,
            {
                title: 'Real World: Add a Nested Config Field',
                description: 'Add a new timeout inside an existing config object',
                patch: '{ "op": "add", "path": "/config/timeouts/read", "value": 30 }',
                before: '{ "config": { "timeouts": { "write": 60 } } }',
                after: '{ "config": { "timeouts": { "write": 60, "read": 30 } } }'
            }
        ],
        useCases: [
            'Adding new fields to user profiles',
            'Inserting items into shopping carts',
            'Appending log entries',
            'Adding new configuration options'
        ],
        bestPractices: [
            'Use "/-" to append to arrays without knowing the length',
            'The parent path must exist before adding nested values',
            'Adding to an existing path will replace the value (use replace for clarity)'
        ],
        faqs: [
            { q: 'Does add overwrite an existing value?', a: 'On an object, adding to a key that already exists replaces its value - but use replace for that, it signals intent. On an array, add never overwrites: it inserts before the index you give.' },
            { q: 'What happens if the parent path does not exist?', a: 'The operation fails and evaluation stops at that point. Every level of the path except the final key must already exist - create parent objects with earlier add operations first.' },
            { q: 'How do I append to an array without knowing its length?', a: 'End the path with /- instead of an index: { "op": "add", "path": "/items/-", "value": ... } always lands at the end.' }
        ]
    },
    remove: {
        title: 'Remove Operation',
        emoji: '➖',
        subtitle: 'Delete values from JSON objects and arrays',
        description: 'The remove operation deletes the value at the specified path. When removing an array element, subsequent elements are shifted to fill the gap. The path must point to an existing value.',
        syntax: '{ "op": "remove", "path": "/path/to/target" }',
        examples: [
            {
                title: 'Remove from Object',
                description: 'Delete a property from an object',
                patch: '{ "op": "remove", "path": "/email" }',
                before: '{ "name": "John", "email": "john@example.com" }',
                after: '{ "name": "John" }'
            },
            {
                title: 'Remove from Array',
                description: 'Delete an item from an array by index',
                patch: '{ "op": "remove", "path": "/items/1" }',
                before: '{ "items": ["a", "b", "c"] }',
                after: '{ "items": ["a", "c"] }'
            },
            {
                title: 'Remove Nested Value',
                description: 'Delete a deeply nested property',
                patch: '{ "op": "remove", "path": "/user/address/zip" }',
                before: '{ "user": { "address": { "city": "NYC", "zip": "10001" } } }',
                after: '{ "user": { "address": { "city": "NYC" } } }'
            }
        ,
            {
                title: 'Real World: Deprecate an API Field',
                description: 'Drop a legacy field from a response payload',
                patch: '{ "op": "remove", "path": "/user/legacy_id" }',
                before: '{ "user": { "id": 7, "legacy_id": "x-99" } }',
                after: '{ "user": { "id": 7 } }'
            }
        ],
        useCases: [
            'Removing items from shopping carts',
            'Deleting user preferences',
            'Cleaning up deprecated fields',
            'Removing array elements'
        ],
        bestPractices: [
            'Always verify the path exists before removing',
            'Consider using test operation before remove for safety',
            'Removing non-existent paths will cause an error'
        ],
        faqs: [
            { q: 'What happens if the path does not exist?', a: 'remove fails and evaluation stops. RFC 6902 does not guarantee rollback of earlier operations. If you are unsure the value is there, guard the remove with a test operation first.' },
            { q: 'Does removing an array element shift the other indexes?', a: 'Yes. Every element after the removed one shifts one position left, so removing several elements in one patch needs care: remove from the highest index down.' },
            { q: 'How do I delete a value but keep the key?', a: 'That is not a remove - use replace with null: { "op": "replace", "path": "/field", "value": null }. The key stays, the value is cleared.' }
        ]
    },
    replace: {
        title: 'Replace Operation',
        emoji: '🔄',
        subtitle: 'Update existing values in JSON documents',
        description: 'The replace operation updates the value at the specified path with a new value. The path must point to an existing value - use add for creating new values. This is semantically equivalent to remove followed by add.',
        syntax: '{ "op": "replace", "path": "/path/to/target", "value": <new value> }',
        examples: [
            {
                title: 'Replace Primitive',
                description: 'Update a simple value',
                patch: '{ "op": "replace", "path": "/age", "value": 30 }',
                before: '{ "name": "John", "age": 25 }',
                after: '{ "name": "John", "age": 30 }'
            },
            {
                title: 'Replace Object',
                description: 'Replace an entire nested object',
                patch: '{ "op": "replace", "path": "/address", "value": { "city": "LA" } }',
                before: '{ "address": { "city": "NYC", "zip": "10001" } }',
                after: '{ "address": { "city": "LA" } }'
            },
            {
                title: 'Replace Array Element',
                description: 'Update a specific array item',
                patch: '{ "op": "replace", "path": "/items/0", "value": "updated" }',
                before: '{ "items": ["old", "b", "c"] }',
                after: '{ "items": ["updated", "b", "c"] }'
            }
        ,
            {
                title: 'Real World: Flip a Feature Flag',
                description: 'Switch a boolean setting in one operation',
                patch: '{ "op": "replace", "path": "/features/beta", "value": true }',
                before: '{ "features": { "beta": false, "dark_mode": true } }',
                after: '{ "features": { "beta": true, "dark_mode": true } }'
            }
        ],
        useCases: [
            'Updating user profile information',
            'Changing configuration values',
            'Modifying prices or quantities',
            'Updating status fields'
        ],
        bestPractices: [
            'Path must exist - use add for new values',
            'Replaces the entire value, including nested structures',
            'Use for clarity over add when updating existing values'
        ],
        faqs: [
            { q: 'What is the difference between replace and add?', a: 'replace requires the path to already exist; add can create new keys. On an existing object key both would update the value, but replace documents the intent and fails loudly if the field is missing.' },
            { q: 'Can replace change the type of a value?', a: 'Yes - replace swaps the entire value, so a string can become an object or a number can become an array. The whole old value is discarded.' },
            { q: 'How do I change just one field of a nested object?', a: 'Point the path at the field, not the object: replace /address/city only touches city. Replacing /address would wipe every other field in that object.' }
        ]
    },
    move: {
        title: 'Move Operation',
        emoji: '📦',
        subtitle: 'Relocate values within JSON documents',
        description: 'The move operation removes the value at the "from" path and adds it at the "path" location. It\'s equivalent to a remove followed by an add. Both paths must be valid - the "from" value must exist.',
        syntax: '{ "op": "move", "from": "/source/path", "path": "/destination/path" }',
        examples: [
            {
                title: 'Move Property',
                description: 'Rename/relocate a property',
                patch: '{ "op": "move", "from": "/old_name", "path": "/new_name" }',
                before: '{ "old_name": "value", "other": 123 }',
                after: '{ "other": 123, "new_name": "value" }'
            },
            {
                title: 'Move to Nested Path',
                description: 'Move a value deeper into the structure',
                patch: '{ "op": "move", "from": "/temp", "path": "/config/setting" }',
                before: '{ "temp": "value", "config": {} }',
                after: '{ "config": { "setting": "value" } }'
            },
            {
                title: 'Reorder Array',
                description: 'Move an array element to a new position',
                patch: '{ "op": "move", "from": "/items/2", "path": "/items/0" }',
                before: '{ "items": ["a", "b", "c"] }',
                after: '{ "items": ["c", "a", "b"] }'
            }
        ,
            {
                title: 'Real World: Promote a Nested Value',
                description: 'Lift a field out of a nested object during a schema migration',
                patch: '{ "op": "move", "from": "/meta/author", "path": "/author" }',
                before: '{ "title": "Post", "meta": { "author": "ana" } }',
                after: '{ "title": "Post", "meta": {}, "author": "ana" }'
            }
        ],
        useCases: [
            'Renaming fields during schema migrations',
            'Reorganizing data structures',
            'Reordering array elements',
            'Moving data between nested objects'
        ],
        bestPractices: [
            'Source path must exist',
            'Cannot move a value to its own descendant',
            'Useful for refactoring JSON structures'
        ],
        faqs: [
            { q: 'What is the difference between move and copy?', a: 'move removes the value from the source location; copy leaves the source untouched. Rename a key with move, duplicate a value with copy.' },
            { q: 'Can I move a value into one of its own children?', a: 'No. Moving /a to /a/b is explicitly forbidden by RFC 6902 and fails - the source would disappear before the destination could be written.' },
            { q: 'Is move atomic?', a: 'Yes. The removal from from and the addition at path happen as one operation - the patch never leaves the value in a half-moved state.' }
        ]
    },
    copy: {
        title: 'Copy Operation',
        emoji: '📋',
        subtitle: 'Duplicate values within JSON documents',
        description: 'The copy operation duplicates the value at the "from" path to the "path" location. Unlike move, the source value remains in place. This is useful for creating backups or duplicating data.',
        syntax: '{ "op": "copy", "from": "/source/path", "path": "/destination/path" }',
        examples: [
            {
                title: 'Copy Property',
                description: 'Duplicate a value to a new location',
                patch: '{ "op": "copy", "from": "/original", "path": "/backup" }',
                before: '{ "original": "data" }',
                after: '{ "original": "data", "backup": "data" }'
            },
            {
                title: 'Copy Nested Object',
                description: 'Duplicate an entire object structure',
                patch: '{ "op": "copy", "from": "/template", "path": "/instances/0" }',
                before: '{ "template": { "type": "default" }, "instances": [] }',
                after: '{ "template": { "type": "default" }, "instances": [{ "type": "default" }] }'
            },
            {
                title: 'Copy Array Element',
                description: 'Duplicate an item within an array',
                patch: '{ "op": "copy", "from": "/items/0", "path": "/items/-" }',
                before: '{ "items": ["original"] }',
                after: '{ "items": ["original", "original"] }'
            }
        ,
            {
                title: 'Real World: Reuse the Billing Address',
                description: 'Default the shipping address to the billing one',
                patch: '{ "op": "copy", "from": "/billing/address", "path": "/shipping/address" }',
                before: '{ "billing": { "address": { "city": "NYC" } } }',
                after: '{ "billing": { "address": { "city": "NYC" } }, "shipping": { "address": { "city": "NYC" } } }'
            }
        ],
        useCases: [
            'Creating backup copies before modifications',
            'Duplicating templates or configurations',
            'Copying shared values to multiple locations',
            'Creating variations based on existing data'
        ],
        bestPractices: [
            'Creates a deep copy of the source value',
            'Source path must exist',
            'Useful for templates and default values'
        ],
        faqs: [
            { q: 'Does copy link the two values?', a: 'No. copy makes a deep, independent clone - changing the copy later does not affect the source, and vice versa.' },
            { q: 'What is the difference between copy and move?', a: 'copy keeps the source value in place; move deletes it. Use copy for defaults and templates, move for renames and restructuring.' },
            { q: 'What happens if the destination already exists?', a: 'The destination follows add semantics: on an existing object key the value is replaced, and the destination parent must exist.' }
        ]
    },
    test: {
        title: 'Test Operation',
        emoji: '✅',
        subtitle: 'Verify values before applying changes',
        description: 'The test operation checks if the value at the specified path matches the expected value. If the test fails, evaluation stops at that operation. This enables conditional patching and optimistic concurrency control.',
        syntax: '{ "op": "test", "path": "/path/to/check", "value": <expected value> }',
        examples: [
            {
                title: 'Test Before Update',
                description: 'Verify a value before replacing it',
                patch: `[
  { "op": "test", "path": "/version", "value": 1 },
  { "op": "replace", "path": "/version", "value": 2 }
]`,
                before: '{ "version": 1, "data": "..." }',
                after: '{ "version": 2, "data": "..." }'
            },
            {
                title: 'Test Object Match',
                description: 'Verify an entire object matches',
                patch: '{ "op": "test", "path": "/user", "value": { "id": 1 } }',
                before: '{ "user": { "id": 1 } }',
                after: 'Patch succeeds if match, fails otherwise'
            },
            {
                title: 'Conditional Delete',
                description: 'Only remove if value matches',
                patch: `[
  { "op": "test", "path": "/status", "value": "pending" },
  { "op": "remove", "path": "/status" }
]`,
                before: '{ "status": "pending" }',
                after: '{ }'
            }
        ,
            {
                title: 'Real World: Optimistic Concurrency Check',
                description: 'Update only if nobody else touched the record',
                patch: `[
  { "op": "test", "path": "/version", "value": 3 },
  { "op": "replace", "path": "/version", "value": 4 },
  { "op": "replace", "path": "/status", "value": "shipped" }
]`,
                before: '{ "version": 3, "status": "packed" }',
                after: '{ "version": 4, "status": "shipped" }'
            }
        ],
        useCases: [
            'Optimistic concurrency control',
            'Conditional updates based on current state',
            'Preventing race conditions',
            'Validating assumptions before changes'
        ],
        bestPractices: [
            'Place test operations before related modifications',
            'Use for version checking and conflict detection',
            'Deep equality comparison for objects and arrays',
            'Essential for safe concurrent updates'
        ],
        faqs: [
            { q: 'What happens when a test operation fails?', a: 'Patch evaluation stops at the failed test. RFC 6902 does not require rollback of earlier operations, so place tests before the writes they guard and verify your implementation.' },
            { q: 'Is the number 1 equal to 1.0 in a test?', a: 'RFC 6902 compares numbers mathematically, so 1 and 1.0 match. But 1 and "1" do not: a number never equals a string, and that type mismatch is the most common test failure.' },
            { q: 'Where should test operations go in a patch?', a: 'Before the operations they guard. Operations run in order, so a test only protects changes that come after it.' }
        ]
    }
};

type Operation = keyof typeof operationsData;

export default function OperationPage({ params }: { params: Promise<{ operation: string }> }) {
    const { operation } = use(params);


    if (!Object.keys(operationsData).includes(operation)) {
        notFound();
    }

    const op = operationsData[operation as Operation];
    const pageKey = `operations-${operation}` as const;

    // Get other operations for navigation
    const allOperations = Object.keys(operationsData) as Operation[];
    const otherOperations = allOperations.filter(o => o !== operation);

    return (
        <>
            <Navigation activePage="operations" />

            <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link href="/" style={{ textDecoration: 'none', color: 'var(--accent)' }}>
                        Home
                    </Link>
                    <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--accent)' }}>
                        Documentation
                    </Link>
                    <Typography color="text.primary">{op.title}</Typography>
                </Breadcrumbs>

                <Typography
                    variant="h1"
                    sx={{
                        fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                        fontWeight: 700,
                        mb: 2,
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <span style={{ fontSize: '1.5em' }}>{op.emoji}</span>
                    JSON Patch {op.title}
                </Typography>

                <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 3 }}>
                    {op.subtitle}
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Chip label="RFC 6902" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label={`${operation} operation`} variant="outlined" color="primary" />
                </Box>

                {/* Description */}
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        What is the {op.title}?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        {op.description}
                    </Typography>

                    <Typography variant="h3" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        Syntax
                    </Typography>
                    <Box sx={{
                        bgcolor: 'var(--code-bg)',
                        color: 'var(--fg)',
                        p: 2,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.95rem',
                        border: '1px solid var(--border)',
                        overflowX: 'auto'
                    }}>
                        {op.syntax}
                    </Box>
                </Paper>

                {/* Examples */}
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 3, color: 'text.primary' }}>
                        Examples
                    </Typography>

                    <Grid container spacing={3}>
                        {op.examples.map((example, index) => (
                            <Grid item xs={12} key={index}>
                                <Box sx={{
                                    p: 3,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2
                                }}>
                                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 1, color: 'text.primary' }}>
                                        {example.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                        {example.description}
                                    </Typography>

                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Patch:
                                    </Typography>
                                    <Box sx={{
                                        bgcolor: 'var(--accent-muted)',
                                        p: 2,
                                        borderRadius: 1,
                                        fontFamily: 'monospace',
                                        fontSize: '0.85rem',
                                        mb: 2,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {example.patch}
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                Before:
                                            </Typography>
                                            <Box sx={{
                                                bgcolor: 'var(--code-bg)',
                                                p: 2,
                                                borderRadius: 1,
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem',
                                                border: '1px solid var(--border)'
                                            }}>
                                                {example.before}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                After:
                                            </Typography>
                                            <Box sx={{
                                                bgcolor: 'var(--success-muted)',
                                                p: 2,
                                                borderRadius: 1,
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem',
                                                border: '1px solid var(--border)'
                                            }}>
                                                {example.after}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Use Cases & Best Practices */}
                <Grid container spacing={4} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, height: '100%' }}>
                            <Typography variant="h2" sx={{ fontSize: '1.3rem', fontWeight: 600, mb: 2, color: 'text.primary' }}>
                                Common Use Cases
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                {op.useCases.map((useCase, index) => (
                                    <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6 }} key={index}>
                                        {useCase}
                                    </Typography>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, height: '100%' }}>
                            <Typography variant="h2" sx={{ fontSize: '1.3rem', fontWeight: 600, mb: 2, color: 'text.primary' }}>
                                Best Practices
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                {op.bestPractices.map((practice, index) => (
                                    <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6 }} key={index}>
                                        {practice}
                                    </Typography>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Array deep dive (add/remove only) */}
                {(operation === 'add' || operation === 'remove') && (
                    <Paper sx={{ p: 4, mb: 4, border: '1px solid var(--border)' }}>
                        <Typography variant="h2" sx={{ fontSize: '1.3rem', fontWeight: 600, mb: 1, color: 'text.primary' }}>
                            Working with arrays?
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.6 }}>
                            {operation === 'add'
                                ? 'Appending with /-, inserting at an index, and why add never overwrites - every array rule with runnable examples.'
                                : 'Index shifting, removing several elements in one patch, and the highest-index-first pattern - every array rule with runnable examples.'}
                        </Typography>
                        <Link href="/docs/json-patch-array" style={{ textDecoration: 'none' }}>
                            <Typography variant="body2" sx={{ color: 'var(--accent)', fontWeight: 600 }}>
                                Read: JSON Patch and Arrays →
                            </Typography>
                        </Link>
                    </Paper>
                )}

                {/* FAQ */}
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h2" sx={{ fontSize: '1.3rem', fontWeight: 600, mb: 3, color: 'text.primary' }}>
                        {op.title} FAQ
                    </Typography>
                    {op.faqs.map((faq, index) => (
                        <Box key={index} sx={{ mb: index < op.faqs.length - 1 ? 3 : 0 }}>
                            <Typography variant="h3" sx={{ fontSize: '1.05rem', fontWeight: 600, mb: 1, color: 'text.primary' }}>
                                {faq.q}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                {faq.a}
                            </Typography>
                        </Box>
                    ))}
                </Paper>

                {/* Try It Now CTA */}
                <Paper sx={{
                    p: 4,
                    mb: 4,
                    textAlign: 'center',
                    background: 'var(--accent)',
                    color: 'white'
                }}>
                    <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2 }}>
                        Try the {op.title} Now
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                        Use our free online JSON Patch generator to create {operation} patches instantly
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        href="/"
                        sx={{
                            backgroundColor: 'white',
                            color: '#1976d2',
                            fontWeight: 600,
                            px: 4,
                            py: 1.5,
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.9)'
                            }
                        }}
                    >
                        🚀 Open JSON Patch Generator
                    </Button>
                </Paper>

                {/* Other Operations */}
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h2" sx={{ fontSize: '1.3rem', fontWeight: 600, mb: 3, color: 'text.primary' }}>
                        Other JSON Patch Operations
                    </Typography>
                    <Grid container spacing={2}>
                        {otherOperations.map((otherOp) => {
                            const opData = operationsData[otherOp];
                            return (
                                <Grid item xs={6} sm={4} md={2} key={otherOp}>
                                    <Link href={`/operations/${otherOp}`} style={{ textDecoration: 'none' }}>
                                        <Box sx={{
                                            p: 2,
                                            textAlign: 'center',
                                            borderRadius: 2,
                                            border: 1,
                                            borderColor: 'var(--border)',
                                            transition: 'transform 0.2s ease-out',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                transform: 'translateY(-2px)'
                                            }
                                        }}>
                                            <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>{opData.emoji}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', textTransform: 'capitalize' }}>
                                                {otherOp}
                                            </Typography>
                                        </Box>
                                    </Link>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>

                {/* Related Pages */}
                <RelatedPages currentPage={pageKey as any} showOperations={false} maxItems={3} />
            </Container>
        </>
    );
}
